// VisitorGatepassVM.js
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const VisitorGatepassVM = () => {
  const { handleToastOpen } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [visitorData, setVisitorData] = useState({});
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [openPhotoPreview, setOpenPhotoPreview] = useState(false);
  const [openDocumentPreview, setOpenDocumentPreview] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [canCheckIn, setCanCheckIn] = useState(false);
  const [canCheckOut, setCanCheckOut] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const userRole = localStorage.getItem("userRole");
    if (userRole !== "Gatekeeper") {
      localStorage.setItem("redirectAfterLogin", window.location.pathname);
      navigate("/visitor-mgmt/login");
      return;
    }

    fetchVisitorData();
  }, [id, navigate]);

  useEffect(() => {
    if (visitorData) {
      updateButtonStates();
    }
  }, [visitorData]);

  const updateButtonStates = () => {
    const status = visitorData.status;
    const checkIn = visitorData.check_in;
    const checkOut = visitorData.check_out;

    // Disable all actions if status is Rejected or Resubmitted
    if (["Rejected", "Resubmitted"].includes(status)) {
      setDisabled(true);
      setCanCheckIn(false);
      setCanCheckOut(false);
      return;
    }

    // Only allow actions if status is Approved
    if (status !== "Approved") {
      setDisabled(true);
      setCanCheckIn(false);
      setCanCheckOut(false);
      return;
    }

    setDisabled(false);

    // Check if visitor is already checked in/out
    const isCheckedIn = !!checkIn;
    const isCheckedOut = !!checkOut;

    setIsCheckedIn(isCheckedIn);

    // Check-in is allowed if:
    // - Status is Approved
    // - Not already checked in
    setCanCheckIn(!isCheckedIn);

    // Check-out is allowed if:
    // - Status is Approved
    // - Already checked in
    // - Not already checked out
    setCanCheckOut(isCheckedIn && !isCheckedOut);
  };

  const fetchVisitorData = async () => {
    try {
      console.log("Fetching visitor data for ID:", id);
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/visitor/${id}`
      );

      console.log("Complete API response:", response.data);

      if (response.data && response.data.success && response.data.data) {
        const visitorDataFromApi = response.data.data;
        console.log("Extracted visitor data:", visitorDataFromApi);
        setVisitorData(visitorDataFromApi);
      } else if (response.data) {
        const visitorDataFromApi = response.data;
        console.log("Using direct visitor data from API:", visitorDataFromApi);
        setVisitorData(visitorDataFromApi);
      } else {
        console.error("Unexpected API response structure:", response.data);
        handleToastOpen("error", "Unexpected data format received from server");
        setVisitorData({});
      }

      setDebugInfo({
        apiResponse: response.data,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error fetching visitor data:", error);
      handleToastOpen("error", "Failed to fetch visitor data");
      setVisitorData({});
    } finally {
      setLoading(false);
    }
  };

  const handleCheckInOut = async (action) => {
    try {
      setLoading(true);
      
      // Get the gatekeeper identity (email or mobile number) from localStorage
      let gatekeeper_id = localStorage.getItem("contactInfo");
      
      if (!gatekeeper_id) {
        handleToastOpen("error", "Gatekeeper identification not found. Please login again.");
        navigate("/visitor-mgmt/login");
        return;
      }
      
      console.log(`Sending ${action} request with gatekeeper_id:`, gatekeeper_id);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/updateCheckInOut/${id}`,
        { 
          action,
          gatekeeper_id
        }
      );

      console.log(`${action} response:`, response.data);

      if (response.data && response.data.notificationStatus) {
        setNotificationStatus(response.data.notificationStatus);
      } else {
        setNotificationStatus(null);
      }

      // Update local state based on action
      if (action === "checkin") {
        setVisitorData(prev => ({
          ...prev,
          check_in: new Date().toISOString()
        }));
      } else if (action === "checkout") {
        setVisitorData(prev => ({
          ...prev,
          check_out: new Date().toISOString()
        }));
      }

      setConfirmationType(action);
      setShowConfirmation(true);

      // Show notification status in toast
      if (response.data.notificationStatus) {
        const notifications = [];
        
        // Check for email notification
        if (response.data.notificationStatus.email) {
          notifications.push("Email");
        }
        
        // Check for SMS notifications
        if (response.data.notificationStatus.sms) {
          if (typeof response.data.notificationStatus.sms === 'object') {
            if (response.data.notificationStatus.sms.visitor) notifications.push("SMS (Visitor)");
            if (response.data.notificationStatus.sms.approver) notifications.push("SMS (Approver)");
          } else if (response.data.notificationStatus.sms === true) {
            notifications.push("SMS");
          }
        }
        
        // Check for WhatsApp notifications
        if (response.data.notificationStatus.whatsapp) {
          if (typeof response.data.notificationStatus.whatsapp === 'object') {
            if (response.data.notificationStatus.whatsapp.visitor) notifications.push("WhatsApp (Visitor)");
            if (response.data.notificationStatus.whatsapp.approver) notifications.push("WhatsApp (Approver)");
          } else if (response.data.notificationStatus.whatsapp === true) {
            notifications.push("WhatsApp");
          }
        }

        if (notifications.length > 0) {
          handleToastOpen(
            "success",
            `Notifications sent via: ${notifications.join(", ")}`
          );
        }
      }
    } catch (error) {
      handleToastOpen("error", `Failed to ${action} visitor`);
      console.error(`Error ${action} visitor:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = () => handleCheckInOut("checkin");
  const handleCheckOut = () => handleCheckInOut("checkout");

  const handleResubmit = async () => {
    try {
      const fe_url = window.location.origin;
      setDisabled(true);
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/updateVisitorStatusAndProcess/${id}?fe_url=${fe_url}`,
        {
          params: {
            status: "Resubmitted",
          },
        }
      );

      console.log("Resubmit response:", response.data);

      if (response.data && response.data.notificationStatus) {
        setNotificationStatus(response.data.notificationStatus);

        // Show notification status in toast
        const { email, sms, whatsapp, whatsapp_resubmit } =
          response.data.notificationStatus;
        const notificationsInfo = [];

        if (email) notificationsInfo.push("Email");
        if (sms) notificationsInfo.push("SMS");
        if (whatsapp || whatsapp_resubmit) notificationsInfo.push("WhatsApp");

        if (notificationsInfo.length > 0) {
          handleToastOpen(
            "success",
            `Notifications sent via: ${notificationsInfo.join(", ")}`
          );
        }
      } else {
        setNotificationStatus(null);
      }

      setVisitorData({ ...visitorData, status: "Resubmitted" });
      setConfirmationType("resubmit");
      setShowConfirmation(true);
    } catch (error) {
      handleToastOpen("error", "Failed to update visitor status");
      console.error("Error updating visitor status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPhotoPreview = () => {
    if (visitorData && visitorData.photo_url) {
      setPhotoPreview(visitorData.photo_url);
      setOpenPhotoPreview(true);
    } else {
      handleToastOpen("error", "Photo not available");
    }
  };

  const handleClosePhotoPreview = () => {
    setOpenPhotoPreview(false);
    setPhotoPreview(null);
  };

  const handleOpenDocumentPreview = () => {
    if (visitorData && visitorData.document_url) {
      setDocumentPreview(visitorData.document_url);
      setOpenDocumentPreview(true);
    } else {
      handleToastOpen("error", "Document not available");
    }
  };

  const handleCloseDocumentPreview = () => {
    setOpenDocumentPreview(false);
    setDocumentPreview(null);
  };

  const handleBackFromConfirmation = () => {
    setShowConfirmation(false);
    setNotificationStatus(null);
  };

  const handleOpenScanner = () => {
    setScannerOpen(true);
  };

  const handleCloseScanner = () => {
    setScannerOpen(false);
  };

  const handleQrCodeScanned = (scannedData) => {
    console.log("QR code scan result:", scannedData);

    handleCloseScanner();

    let visitorId = scannedData;

    try {
      if (scannedData.includes("http")) {
        const url = new URL(scannedData);

        const pathSegments = url.pathname
          .split("/")
          .filter((segment) => segment);
        if (pathSegments.length > 0) {
          const potentialId = pathSegments[pathSegments.length - 1];
          if (potentialId && /^[a-zA-Z0-9-_]+$/.test(potentialId)) {
            visitorId = potentialId;
          }
        }

        const urlParams = new URLSearchParams(url.search);
        if (urlParams.has("id")) {
          visitorId = urlParams.get("id");
        }

        console.log("Extracted ID from URL:", visitorId);
      }
    } catch (error) {
      console.error("Error processing QR URL:", error);
    }

    if (visitorId !== id) {
      navigate(`/visitor-mgmt/gatepass/${visitorId}`);
    } else {
      fetchVisitorData();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";

    try {
      const options = { 
        year: "numeric", 
        month: "long", 
        day: "numeric",
        timeZone: "Africa/Brazzaville" // Add timezone for consistent local time
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  return {
    id,
    visitorData,
    loading,
    disabled,
    openPhotoPreview,
    openDocumentPreview,
    photoPreview,
    documentPreview,
    canCheckIn,
    canCheckOut,
    scannerOpen,
    showConfirmation,
    confirmationType,
    isCheckedIn,
    notificationStatus,
    debugInfo,
    handleCheckIn,
    handleCheckOut,
    handleResubmit,
    handleOpenPhotoPreview,
    handleClosePhotoPreview,
    handleOpenDocumentPreview,
    handleCloseDocumentPreview,
    handleBackFromConfirmation,
    handleOpenScanner,
    handleCloseScanner,
    handleQrCodeScanned,
    formatDate,
  };
};

export default VisitorGatepassVM;